import * as React from 'react';
import PropTypes from 'prop-types';

import { uiConfigTable, uiConfigReadOnly } from './conf';

// Components
import Table from '../../../../../../../../../../../components/Table';
import { Button } from '../../../../../../../../../../../components/Button';
import { ConceptSelectionModal } from '../ConceptSelectionModal';
import { CodeConceptFormModal } from '../CodeConceptFormModal';

// Context
import { CriteriaStoreContext } from '../../../../../../../../../state/criteria/context';

// Enums
import { dtoState } from '../../../../../../../../../../../common/enums/dtoState';

// Hooks
import { useConceptCollection } from '../../../../../../../../../api/hooks/useConceptCollection';

import { translateUIConfigForTable } from '../../../../../../../../../../../common/utility';

export const ConceptSelectionTable = ({
    constraintType,
    queryParam,
    selectedConcepts,
    applyPredicatePartConceptsUpdate,
    partId
}) => {
    const { sessionToken } = React.useContext(CriteriaStoreContext);
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [viewing, setViewing] = React.useState(null);
    const conceptCollectionQuery = useConceptCollection({
        sessionToken,
        queryParam,
        constraintType: constraintType
    });
    const setDeleting = () => {};
    console.log('selectedConcepts', selectedConcepts);
    const viewingConceptDto = React.useMemo(() => {
        return (selectedConcepts ?? []).find(({ openCdsConceptDTO }) => openCdsConceptDTO?.codeId === viewing)
            ?.openCdsConceptDTO;
    }, [selectedConcepts, viewing]);
    const { columns, actions } = translateUIConfigForTable(uiConfigTable, setViewing, setDeleting, null);
    const tableData = React.useMemo(() => {
        if (!selectedConcepts) return [];
        if (actions != null) {
            return actions != null
                ? selectedConcepts
                      .filter((concept) => concept.dtoState !== dtoState.Deleted)
                      .map((v) => ({
                          ...v,
                          DataTableActions: actions.map((i) => ({
                              ...i,
                              action: () => i.action(v?.openCdsConceptDTO?.[uiConfigTable.primaryKey])
                          }))
                      }))
                : selectedConcepts;
        }
    }, [selectedConcepts, actions]);
    const modifiedColumns = React.useMemo(() => {
        return (columns ?? []).map((col) => {
            if (col?.accessor === 'DataTableActions') {
                return {
                    ...col,
                    Header: (
                        <Button
                            size="sm"
                            key={'add-new-part'}
                            appearance="primary-dark"
                            onClick={() => {
                                setShowEditModal(true);
                            }}
                        >
                            Edit Selection
                        </Button>
                    ),
                    headerRightAlign: true
                };
            } else {
                return col;
            }
        });
    }, [columns]);

    React.useEffect(() => {
        conceptCollectionQuery?.refetch?.();
    }, [queryParam]);

    return (
        <>
            <Table
                withHeader={false}
                columns={modifiedColumns}
                data={tableData}
                withActions={actions !== null}
                id={'concept-determination-methods-manager'}
                label={'Concept Determination Methods Manager'}
                showControlDeck={false}
                paginated={false}
                loading={conceptCollectionQuery?.isLoading}
                controlButtons={[]}
                hiddenControls={{
                    pageSize: true
                }}
                tableProps={{
                    style: { borderCollapse: 'separate', minHeight: '0' }
                }}
                emptyStateProps={{
                    customLabel: 'No concepts selected.',
                    labelFontSize: 500,
                    imageSize: 3.5
                }}
            />
            {showEditModal ? (
                <ConceptSelectionModal
                    closeDialog={() => {
                        setShowEditModal(false);
                    }}
                    allItems={conceptCollectionQuery?.data ?? []}
                    currentListItems={selectedConcepts ?? []}
                    partId={partId}
                    onApply={(openCdsConceptDTOUpdated) => {
                        applyPredicatePartConceptsUpdate(openCdsConceptDTOUpdated);
                    }}
                />
            ) : null}
            {viewingConceptDto && (
                <CodeConceptFormModal
                    title={'Concept Details'}
                    data={viewingConceptDto}
                    uiConfig={uiConfigReadOnly}
                    partId={partId}
                    closeDialog={() => {
                        setViewing(null);
                    }}
                />
            )}
        </>
    );
};

ConceptSelectionTable.propTypes = {
    onSelect: PropTypes.func,
    constraintType: PropTypes.oneOf(['Code', 'CodeSystem', 'Concept', 'List', 'ValueSet']),
    queryParam: PropTypes.string,
    partId: PropTypes.string,
    selectedConcepts: PropTypes.arrayOf(PropTypes.object),
    applyPredicatePartConceptsUpdate: PropTypes.func
};

export default ConceptSelectionTable;
